/** @jsx jsx */
import * as React from "react";
import { jsx } from "@emotion/core";
import { diff } from 'deep-object-diff';

import * as styles from './style';
import CTA, { CTATheme } from "../../../components/common/CallToAction";
import { IAppState } from "../../../models/IAppState";
import { IMapStateToProps, IMapDispatchToProps, IFitnessTemplateProps, IMicrocycle, IMicrocycleError, formFieldNames, IUserFitnessPlan, IPaginatedMicrocycles } from "./interface";
import { connect } from "react-redux";
import { PatientInformation } from "../../../components/FitnessPlanTemplate/PatientInformation";
import { get, isEmpty } from "lodash";
import { FITNESS_GOAL, EQUIPMENT, DAYS_OF_WEEK, WORKOUT_PHASES, WORKOUT_FOCUS } from "../constants";
import { validateMicrocycleData, createRequestBody, getInitialWodValues, getSetValue, formatUserPlanToFitnessTemplateObject, formatFitnessTemplateToUserPlanObject, validateMicrocycleinDraftState } from "./helper";
import { MicrocyleTemplate } from "../../../components/FitnessPlanTemplate/MicrocycleTemplate/microcycleTemplate";
import { UserFitnessActions } from "../../../actions/UserFitnessActions";
import _ = require("lodash");
import { FitnessGoal } from "../../../components/FitnessPlanTemplate/FitnessGoalHeader/FitnessGoal";
import { WorkoutNotification } from "../../../components/FitnessPlanTemplate/MicrocycleTemplate/Notification/Notification";
import { IUserFitnessPlanRequestParams } from "../../Patient/Transform/interface";
import { IUserFitnessPlanResponse } from "../../../models/userFitness/IUserFitnessPlan";
import { CustomConfirmationModal } from "../../../components/FitnessPlanTemplate/MicrocycleTemplate/CustomConfirmationModal/CustomConfirmationModal";
import { RouteUtils, FITNESS_PLAN } from "../../../routes";
import AppUtils from "../../../utils/AppUtils";


const FitnessPlanTemplate: React.FC<IFitnessTemplateProps> = (props) => {
    const { userProfile, fetchAllExercises, fetchFitnessTemplateById, saveFitnessTemplate, accountData, allExercises, params, userFitnessPlan, userFitnessPlanStartDate, assignFitnessPlan, isUserPlanPublished, savePublishedUserFitnessPlan, setUserFitnessPlan } = props;

    const [initialMicrocycleDataCopy, setInitialMicrocycleDataCopy] = React.useState<IMicrocycle>(null)
    const [microcycleData, setMicroCycleData] = React.useState<IMicrocycle>(null);
    const [errorStates, setError] = React.useState<IMicrocycleError[]>(null);
    const [isDataFetched, setDataFetched] = React.useState(false);
    const [showFailedNotification, setFailedNotificationDisplay] = React.useState(false);
    const [showDiscardModal, setDiscardModalDisplay] = React.useState(false);
    const [showSuccessNotification, setSuccessNotificationDisplay] = React.useState(false);
    const [isPatientPlan, setIsPatientPlan] = React.useState(false);
    const [isUserPlanEditMode, setIsUserPlanEditMode] = React.useState(false);
    const [isViewMode, setViewMode] = React.useState(false);

    const userDetails = {
        [formFieldNames.USERPLANSTARTDATE]: new Date(userFitnessPlanStartDate).toISOString(),
        [formFieldNames.USERID]: params.userId
    }

    /*  Constraints for user plan
        1. If plan is new, call save plan api twice. One with user id and start date details and the other without details
        2. If editing a user plan, make only one save call with user id and start date
    */

    React.useEffect(() => {
        if (!allExercises) {
            fetchAllExercises();
        }

        //is patient plan 
        if (params.userId) {
            setIsPatientPlan(true);
            //if editing a already assigned plan
            if (params.fitnessTemplateId) {
                //object for user published plan is different. Format it to make it same as 
                if (isUserPlanPublished) {
                    fetchFitnessTemplateById(userFitnessPlan.microCycleEntry.managedMicrocycleId).then((response: IPaginatedMicrocycles) => {
                        const managedMicrocycle: IMicrocycle = _.get(response, 'elements[0]', null);
                        const reformatedMicrocycleData = formatUserPlanToFitnessTemplateObject(userFitnessPlan, managedMicrocycle);
                        setIsUserPlanEditMode(true);
                        setInitialMicrocycleDataCopy(reformatedMicrocycleData);
                        formatMicrocycleResponse(reformatedMicrocycleData);
                        setDataFetched(true);
                    })
                }
                else {
                    setDataFetched(true);
                    setIsUserPlanEditMode(true);
                    setInitialMicrocycleDataCopy(userFitnessPlan);
                    formatMicrocycleResponse(userFitnessPlan);
                }
            }
            else {
                setDataFetched(true);
                createInitialWodTemplate();
            }
        }
        // is template plan
        else if (params.fitnessTemplateId) {
            if (window.location.pathname.indexOf("view") > -1) {
                getMicrocycleDataById(params.fitnessTemplateId);
                setViewMode(true);
            }
            else {
                setIsUserPlanEditMode(true);
                getMicrocycleDataById(params.fitnessTemplateId);
            }
        }
        else {
            createInitialWodTemplate();
        }
        return () => setUserFitnessPlan(null, null, null, null);
    }, []);

    const createInitialWodTemplate = () => {
        //get prewods value for all the days
        const preWodsData = DAYS_OF_WEEK.map(day => {
            return getInitialWodValues(day.internalValue);
        })

        const initialState: IMicrocycle = {
            tenantId: 2,
            regimenId: EQUIPMENT[1].internalValue,
            goalId: FITNESS_GOAL[0].internalValue,
            level: undefined,
            workoutDays: undefined,
            description: undefined,
            rolloutPercentage: 0,
            prewods: preWodsData,
            meta: {
                gender: undefined
            }
        };
        setMicroCycleData(initialState);
        setInitialMicrocycleDataCopy(initialState);
        setDataFetched(true);
    }

    const saveTemplateAsDraft = () => {
        setFailedNotificationDisplay(false);
        setDiscardModalDisplay(false);
        const errorStates = validateMicrocycleinDraftState({ ...microcycleData });
        if (errorStates.length > 0) {
            setError(errorStates);
        }
        else {
            setError(null);
            let requestBody = createRequestBody(microcycleData, accountData.id, true);

            //modify the request body if saving a already assigned plan
            if (isPatientPlan && isUserPlanEditMode) {
                requestBody = { ...requestBody, ...userDetails }
                console.log(requestBody)
            }

            saveFitnessTemplate(requestBody).then((response: IMicrocycle) => {
                if (isPatientPlan && !isUserPlanEditMode) {
                    //assigning managed plan first time to the user
                    const reqParams: IUserFitnessPlanRequestParams = {
                        userPlanStartDate: userFitnessPlanStartDate,
                        userId: params.userId,
                        createdBy: accountData.id
                    }
                    assignFitnessPlan(reqParams, response.id).then(microcycle => {
                        setSuccessNotificationDisplay(true);
                        getMicrocycleDataById(microcycle.id);
                        setIsUserPlanEditMode(true);
                    })
                }
                else {
                    setSuccessNotificationDisplay(true);
                    getMicrocycleDataById(response.id);
                }
            }).catch((err: Error) => {
                console.log(err);
                setFailedNotificationDisplay(true);
            })
        }
    }

    const saveTemplate = () => {
        setFailedNotificationDisplay(false);
        const errorStates = validateMicrocycleData({ ...microcycleData });
        if (errorStates.length > 0) {
            setError(errorStates);
        }
        else {
            let requestBody = createRequestBody(microcycleData, accountData.id, false);

            if (isUserPlanPublished) {
                let formattedReqBody = formatFitnessTemplateToUserPlanObject(requestBody, userFitnessPlan);
                savePublishedUserFitnessPlan(formattedReqBody).then((response: IUserFitnessPlan) => {
                    handleNavigation();
                }).catch((err: Error) => {
                    console.log(err);
                    setFailedNotificationDisplay(true);
                })
            }
            else {
                //modify the request body if saving a already assigned plan
                if (isPatientPlan && isUserPlanEditMode) {
                    requestBody = { ...requestBody, ...userDetails }
                }

                saveFitnessTemplate(requestBody).then((response: IMicrocycle) => {
                    if (isPatientPlan && !isUserPlanEditMode) {
                        //assigning managed plan first time to the user
                        const reqParams: IUserFitnessPlanRequestParams = {
                            userPlanStartDate: userFitnessPlanStartDate,
                            userId: params.userId,
                            createdBy: accountData.id
                        }
                        assignFitnessPlan(reqParams, response.id);
                    }
                    handleNavigation();
                }).catch((err: Error) => {
                    console.log(err);
                    setFailedNotificationDisplay(true);
                })
            }
        }
    }



    const checkForDiscardTemplate = () => {
        //check for any unsaved changes
        const difference = diff(microcycleData, initialMicrocycleDataCopy);

        if (!isEmpty(difference)) {
            setDiscardModalDisplay(true);
        }
        else {
            discardTemplate();
        }
    }

    const discardTemplate = () => {
        handleNavigation()
    }

    const handleNavigation = () => {
        if (params.userId) {
            const userUrl = AppUtils.getPatienTransfomrUrl(Number(userProfile.id));
            RouteUtils.navigateToUrl(userUrl)
        }
        else {
            RouteUtils.navigateToUrl(FITNESS_PLAN);
        }
    }

    const getMicrocycleDataById = (templateId: number) => {
        fetchFitnessTemplateById(templateId).then((response: IPaginatedMicrocycles) => {
            const microCycleData: IMicrocycle = _.get(response, 'elements[0]', null);
            setInitialMicrocycleDataCopy({ ...microCycleData });
            formatMicrocycleResponse(microCycleData);
            setDataFetched(true);
        }).catch((err: Error) => console.log(`Error while fetching fitness template, ${err}`))
    }

    const formatMicrocycleResponse = (microCycleData: IMicrocycle) => {
        microCycleData.prewods.map(wod => {
            //check for missing phases if workout type is not rest day
            if (wod.workoutType != WORKOUT_FOCUS.RestDay) {
                const modifiedSets = wod.sets ? [...wod.sets] : [];

                let missingPhases = WORKOUT_PHASES.filter(phase => {
                    return !modifiedSets.some(set => {
                        return phase == set.blockType;
                    });
                });

                if (missingPhases.length > 0) {
                    const missingSets = missingPhases.map(phase => getSetValue(phase));
                    modifiedSets.push(...missingSets);
                    //rearrange the order
                }
                modifiedSets.sort((a, b) => WORKOUT_PHASES.indexOf(a.blockType) - WORKOUT_PHASES.indexOf(b.blockType));
                wod.sets = modifiedSets;
            }
        })
        setMicroCycleData(microCycleData);
    }

    const handleEditPlan = () => {
        //TODO - useEffect not getting call on url change
        setIsUserPlanEditMode(true); setViewMode(false);
        RouteUtils.navigateToHrefUrl(null, RouteUtils.getEditFitnessPlanUrl(microcycleData.id.toString()));
    }

    return (
        <React.Fragment >
            {isDataFetched &&
                <React.Fragment>
                    {isPatientPlan &&
                        <PatientInformation patientInfo={userProfile} handleBackAction={checkForDiscardTemplate} />
                    }
                    <main css={styles.fitnessPlanOuterContainer}>
                        <FitnessGoal microcycleData={microcycleData} setMicrocyleData={setMicroCycleData} errorStates={errorStates} handleBackAction={checkForDiscardTemplate} isPatientPlan={isPatientPlan} isUserPlanPublished={isUserPlanPublished} isViewMode={isViewMode} handleEditPlan={handleEditPlan} />
                        <MicrocyleTemplate microcycleData={microcycleData} setMicrocyleData={setMicroCycleData} exerciseList={allExercises} errorStates={errorStates} isViewMode={isViewMode} />
                    </main>
                    {!isViewMode &&
                        <footer css={styles.fitnessActions}>
                            {!isUserPlanPublished &&
                                <div css={styles.secondaryButton} onClick={saveTemplateAsDraft}>
                                    <img src="/images/draft.svg"></img>
                                    <span css={styles.secondaryButtonText}>Save Draft</span>
                                </div>
                            }
                            <CTA innerText="Cancel" theme={CTATheme.LIGHT} onClick={checkForDiscardTemplate} customStyle={styles.cancelButtonStyles} />
                            <CTA innerText="Save" theme={CTATheme.PRIMARY} onClick={saveTemplate} customStyle={styles.buttonStyles} />
                        </footer>
                    }
                </React.Fragment>
            }
            {
                showFailedNotification &&
                <WorkoutNotification handleActionClick={saveTemplate} message="Could not save changes  in the WOD plan !" actionText="Retry" />
            }
            {
                showDiscardModal &&
                <CustomConfirmationModal
                    handleCancelAction={() => setDiscardModalDisplay(false)}
                    discardAction={discardTemplate}
                    confirmAction={isUserPlanPublished ? saveTemplate : saveTemplateAsDraft}
                    showCancelOption={true}
                    modalText={{
                        header: "Unsaved Changes!",
                        message: "You have made changes to WOD. Do you want to Save or Discard Them?",
                        discard: "Discard",
                        confirm: "Save",
                    }}
                />
            }
            {
                showSuccessNotification &&
                <WorkoutNotification handleActionClick={() => setSuccessNotificationDisplay(false)} message="WOD plan saved successfully!" actionText="Dismiss" />
            }

        </React.Fragment >
    )
}

const mapStateToProps = (state: IAppState): IMapStateToProps => ({
    userProfile: get(state, "fitnessData.userProfile", null),
    accountData: state.accountData,
    allExercises: get(state, "fitnessData.exercises", null),
    userFitnessPlan: get(state, "fitnessData.userFitnessPlan", null),
    userFitnessPlanStartDate: get(state, "fitnessData.userFitnessPlanStartDate", null),
    isUserPlanPublished: get(state, "fitnessData.isPublishedPlan", false)
});

const mapDispatchToProps = (dispatch: React.Dispatch<any>): IMapDispatchToProps => ({
    fetchFitnessTemplateById: (microcycleId: number) => UserFitnessActions.fetchMicrocyleById(microcycleId),
    fetchAllExercises: () => UserFitnessActions.fetchAllExercises(dispatch),
    saveFitnessTemplate: (requestBody: IMicrocycle) => UserFitnessActions.saveFitnessTemplate(requestBody),
    assignFitnessPlan: (requestParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => UserFitnessActions.assignFitnessPlan(requestParams, selectedPlanId),
    savePublishedUserFitnessPlan: (reqBody: IUserFitnessPlan) => UserFitnessActions.savePublishedUserFitnessPlan(reqBody),
    setUserFitnessPlan: (userPlan: any, startDate: string | number, userProfile: any, isPlanPublished: boolean) => UserFitnessActions.setUserFitnessPlan(userPlan, startDate, userProfile, isPlanPublished, dispatch)
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FitnessPlanTemplate);
